import clsx from 'clsx';
import React from 'react';
import { getSidebarTabId } from './SidebarTabs';

type SidebarTabPanelProps = Readonly<{
  activeTabId: string;
  children?: React.ReactNode;
  className?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  panelId: string;
  tabIndex?: 0;
}>;

/**
 * Panel asociado a SidebarTabs. Cierra la referencia bidireccional del patrón
 * ARIA tabs: expone `role="tabpanel"`, su propio `id` (al que apunta el
 * `aria-controls` de cada tab) y `aria-labelledby` hacia el tab activo vía
 * `getSidebarTabId`. Encapsula la convención de ids para que el consumidor no
 * la reconstruya a mano — mismo patrón que `AccordionPanel`.
 *
 * `tabIndex` queda en manos del consumidor porque APG solo lo pide cuando el
 * panel no tiene contenido enfocable, y eso depende de su estado: un panel con
 * lista pintada no debe añadir una parada de tabulación de más, pero el mismo
 * panel en carga o vacío sí, o `Tab` se lo salta entero. Resuélvelo con
 * `useTabPanelTabIndex` (`core/hooks`) y cablea aquí sus tres valores:
 * `tabIndex={panelTabIndex}`, `onFocus={onPanelFocus}`, `onBlur={onPanelBlur}`.
 *
 * La clase base `sidebar-tabs__panel` existe para reponer el foco visible: el
 * reset global (`*:focus { outline: 0 }`) lo borra, y sin ella el panel recibe
 * el foco sin que se vea nada — WCAG 2.4.7.
 */
function SidebarTabPanel({ activeTabId, children, className, onBlur, onFocus, panelId, tabIndex }: SidebarTabPanelProps): JSX.Element {
  return (
    <div
      aria-labelledby={getSidebarTabId(activeTabId)}
      className={clsx('sidebar-tabs__panel', className)}
      id={panelId}
      onBlur={onBlur}
      onFocus={onFocus}
      role="tabpanel"
      tabIndex={tabIndex}
    >
      {children}
    </div>
  );
}

export default SidebarTabPanel;
